<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  modelValue: {
    type: String,
    default: 'all',
  },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();
const route = useRoute();
const router = useRouter();

const labels = useMapGetter('labels/getLabels');

// The active pill is either 'unread', 'all', or a label title (if route has label)
const activePill = computed(() => {
  if (route.params.label) return `label_${route.params.label}`;
  return props.modelValue;
});

const selectPill = (type, value = null) => {
  if (type === 'all') {
    if (route.params.label) {
      router.push({ name: 'home' });
    }
    emit('update:modelValue', 'all');
  } else if (type === 'unread') {
    if (route.params.label) {
      router.push({ name: 'home' }); 
    }
    emit('update:modelValue', 'unread');
  } else if (type === 'label') {
    router.push({ name: 'label_conversations', params: { label: value.title } });
    emit('update:modelValue', 'all'); 
  }
};

// Modal Logic
const showModal = ref(false);
const newLabelName = ref('');
const newLabelColor = ref('#FF5733'); 

const presetColors = [
  '#FF5733', // Red
  '#335BFF', // Blue
  '#33FF57', // Green
  '#FFD133', // Yellow
  '#A833FF', // Purple
  '#FF33A8', // Pink
  '#33FFF6', // Cyan
  '#FF8C33', // Orange
];

const isCreating = ref(false);

const createLabel = async () => {
  if (!newLabelName.value.trim()) return;
  isCreating.value = true;
  try {
    await store.dispatch('labels/create', {
      title: newLabelName.value.trim(),
      color: newLabelColor.value,
      description: '',
      show_on_sidebar: true,
    });
    showModal.value = false;
    newLabelName.value = '';
  } catch (error) {
    console.error('Error creating label', error);
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="pill-filter-bar-container">
    <div class="pill-scroll-area">
      <!-- ALL -->
      <button 
        class="pill-btn pill-btn-default" 
        :class="{ 'active': activePill === 'all' }"
        @click="selectPill('all')"
      >
        All
      </button>

      <!-- UNREAD -->
      <button 
        class="pill-btn pill-btn-default" 
        :class="{ 'active': activePill === 'unread' }"
        @click="selectPill('unread')"
      >
        Unread
      </button>

      <!-- LABELS -->
      <button 
        v-for="label in labels" 
        :key="label.id"
        class="pill-btn pill-btn-label"
        :class="{ 'active': activePill === `label_${label.title}` }"
        :style="{ 
          '--label-color': label.color, 
          borderColor: activePill === `label_${label.title}` ? label.color : 'rgba(255, 255, 255, 0.1)',
          color: activePill === `label_${label.title}` ? label.color : 'rgba(255, 255, 255, 0.7)',
          background: activePill === `label_${label.title}` ? `${label.color}20` : 'rgba(255, 255, 255, 0.05)',
          boxShadow: activePill === `label_${label.title}` ? `0 0 10px ${label.color}50` : 'none',
          textShadow: activePill === `label_${label.title}` ? `0 0 10px ${label.color}80` : 'none'
        }"
        @click="selectPill('label', label)"
      >
        <span class="label-dot" :style="{ backgroundColor: label.color }"></span>
        {{ label.title }}
      </button>

      <!-- ADD LABEL -->
      <button class="pill-btn pill-btn-add" @click="showModal = true">
        <fluent-icon icon="add" size="14" />
      </button>
    </div>

    <!-- CREATE LABEL MODAL -->
    <woot-modal v-model:show="showModal" :on-close="() => showModal = false">
      <woot-modal-header
        header-title="Create new label"
        header-content="Add a new custom label to organize your conversations."
      />
      <form class="modal-content" @submit.prevent="createLabel">
        <div class="input-group">
          <label>Label Name</label>
          <input 
            type="text" 
            v-model="newLabelName" 
            placeholder="e.g. VIP Customer" 
            required
            class="glass-input"
            autofocus
          />
        </div>
        
        <div class="input-group">
          <label>Select Color</label>
          <div class="color-picker-circles">
            <button
              type="button"
              v-for="color in presetColors"
              :key="color"
              class="color-circle"
              :class="{ 'selected': newLabelColor === color }"
              :style="{ backgroundColor: color }"
              @click="newLabelColor = color"
            ></button>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-cancel" @click="showModal = false">Cancel</button>
          <button type="submit" class="btn-submit" :disabled="isCreating || !newLabelName.trim()">
            {{ isCreating ? 'Creating...' : 'Create Label' }}
          </button>
        </div>
      </form>
    </woot-modal>
  </div>
</template>

<style scoped lang="scss">
.pill-filter-bar-container {
  width: 100%;
  padding: 12px 16px 8px 16px;
  background: transparent;
  border-bottom: none;
}

.pill-scroll-area {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
}

.pill-btn {
  white-space: nowrap;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  backdrop-filter: blur(10px);
}

/* Default state for 'All' and 'Unread' */
.pill-btn-default {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: rgba(231, 247, 2, 0.1);
    border-color: #E7F702;
    color: #E7F702;
    box-shadow: 0 0 10px rgba(231, 247, 2, 0.3);
    text-shadow: 0 0 10px rgba(231, 247, 2, 0.5);
  }
}

/* Label specific styles */
.pill-btn-label {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--label-color) !important;
  }
}

.label-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

/* Add Label Button */
.pill-btn-add {
  background: rgba(255, 255, 255, 0.05);
  border: 1px dashed rgba(255, 255, 255, 0.3);
  color: var(--color-text-2);
  padding: 6px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: rgba(255, 255, 255, 0.1);
  }
}

/* Custom Modal Styling for Cyber-Glassmorphism vibe */
.modal-content {
  padding: 0 32px 32px;
  
  .input-group {
    margin-bottom: 24px;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      color: var(--color-text-1);
    }
  }
  
  .glass-input {
    width: 100%;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
    color: var(--color-text-1);
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s ease;
    
    &:focus {
      border-color: var(--color-primary);
    }
  }
  
  .color-picker-circles {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    
    .color-circle {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 2px solid transparent;
      cursor: pointer;
      transition: transform 0.2s ease, border-color 0.2s ease;
      padding: 0;
      
      &:hover {
        transform: scale(1.1);
      }
      
      &.selected {
        border-color: white;
        transform: scale(1.1);
        box-shadow: 0 0 10px rgba(255,255,255,0.3);
      }
    }
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    
    button {
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    .btn-cancel {
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: var(--color-text-2);
      
      &:hover {
        background: rgba(255, 255, 255, 0.05);
      }
    }
    
    .btn-submit {
      background: var(--color-primary);
      border: 1px solid var(--color-primary);
      color: white;
      
      &:hover:not(:disabled) {
        filter: brightness(1.1);
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
